import React, { useEffect, useState, useCallback } from 'react';
import { DateTimeInput } from '@/components/ui/date-input';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, User, Mail, Phone, DollarSign, Calendar, Clock, 
  MessageSquare, Paperclip, Bell, Edit2, Trash2, Send, Upload,
  UserPlus, FileText, AlertCircle, Package, ShoppingCart, Plus, Percent, Truck, CreditCard,
  Link2, Copy, CheckCircle, RefreshCw, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { formatDate, formatDateTime, getLeadStageColor } from '@/lib/utils';
import PaymentLinkButton from '@/components/PaymentLinkButton';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LEAD_STAGES = [
  { id: 'new', label: 'New', color: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
  { id: 'quotation_sent', label: 'Quotation Sent', color: 'bg-purple-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
  { id: 'closed_won', label: 'Closed Won', color: 'bg-green-500' },
  { id: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500' },
];

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesUsers, setSalesUsers] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Check if user is admin (both is_admin flag and legacy roles check)
  const isAdmin = user?.is_admin || user?.roles?.includes('admin');
  const canCreatePaymentLink = isAdmin || userPermissions.includes('mamopay.create_link');
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [newComment, setNewComment] = useState('');
  const [newReminder, setNewReminder] = useState({ datetime: '', message: '' });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Products states
  const [inventory, setInventory] = useState([]);
  const [searchProduct, setSearchProduct] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [leadProducts, setLeadProducts] = useState([]);
  const [savingProducts, setSavingProducts] = useState(false);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState(0);
  const [customCharges, setCustomCharges] = useState([]);
  const [newCustomCharge, setNewCustomCharge] = useState({ name: '', amount: 0 });
  
  // Shipping/Billing Address states (saved with lead)
  const [leadShippingAddress, setLeadShippingAddress] = useState({
    address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
  });
  const [leadBillingAddress, setLeadBillingAddress] = useState({
    address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
  });
  const [sameAsShipping, setSameAsShipping] = useState(true);

  // Create Order states
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod'); // 'already_paid', 'cod', 'request_payment'
  const [paymentNotes, setPaymentNotes] = useState('');
  const [mamoPayLink, setMamoPayLink] = useState(null);
  const [mamoPayLinkCreating, setMamoPayLinkCreating] = useState(false);
  const [mamoPayLinkPaid, setMamoPayLinkPaid] = useState(false);
  
  // Payment link status check state
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState({});
  const [orderForm, setOrderForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: {
      first_name: '',
      last_name: '',
      address1: '',
      address2: '',
      city: '',
      province: '',
      country: 'United Arab Emirates',
      zip: ''
    },
    sync_to_shopify: true,
    payment_status: 'pending',
    notes: ''
  });

  const loadLead = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLead(response.data);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadSalesUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/sales-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalesUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load sales users');
    }
  }, [isAdmin]);

  const loadInventory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInventory(response.data || []);
    } catch (error) {
      console.error('Failed to load inventory');
    }
  }, []);

  useEffect(() => {
    loadLead();
    loadSalesUsers();
    loadUserPermissions();
  }, [loadLead, loadSalesUsers]);

  const loadUserPermissions = async () => {
    if (!user?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users/${user.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to load permissions');
    }
  };

  // Load inventory when products dialog opens
  useEffect(() => {
    if (productsDialogOpen) {
      loadInventory();
      setLeadProducts(lead?.products || []);
      setShippingCharge(lead?.shipping_charge || 0);
      setDiscountType(lead?.discount_type || '');
      setDiscountValue(lead?.discount_value || 0);
      setCustomCharges(lead?.custom_charges || []);
      // Load shipping/billing addresses
      setLeadShippingAddress(lead?.shipping_address || {
        address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
      });
      setLeadBillingAddress(lead?.billing_address || {
        address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
      });
      setSameAsShipping(lead?.billing_same_as_shipping !== false);
    }
  }, [productsDialogOpen, lead?.products, lead?.shipping_charge, lead?.discount_type, lead?.discount_value, lead?.custom_charges, lead?.shipping_address, lead?.billing_address, lead?.billing_same_as_shipping, loadInventory]);

  // Product management functions
  const addProductToLead = (item) => {
    const existingIndex = leadProducts.findIndex(p => p.product_id === item.id);
    if (existingIndex >= 0) {
      const updated = [...leadProducts];
      updated[existingIndex].quantity += 1;
      updated[existingIndex].total = 
        (updated[existingIndex].unit_price * updated[existingIndex].quantity) + 
        (updated[existingIndex].customization_charge || 0);
      setLeadProducts(updated);
    } else {
      const newProduct = {
        product_id: item.id,
        sku: item.sku,
        name: item.name,
        quantity: 1,
        unit_price: item.selling_price || 0,
        customization_charge: 0,
        total: item.selling_price || 0,
      };
      setLeadProducts([...leadProducts, newProduct]);
    }
    setSearchProduct('');
  };

  const updateLeadProduct = (index, field, value) => {
    const updated = [...leadProducts];
    updated[index][field] = value;
    updated[index].total = 
      (updated[index].unit_price * updated[index].quantity) + 
      (updated[index].customization_charge || 0);
    setLeadProducts(updated);
  };

  const removeLeadProduct = (index) => {
    setLeadProducts(leadProducts.filter((_, i) => i !== index));
  };

  const addCustomChargeToLead = () => {
    if (newCustomCharge.name && newCustomCharge.amount > 0) {
      setCustomCharges([...customCharges, { ...newCustomCharge }]);
      setNewCustomCharge({ name: '', amount: 0 });
    }
  };

  const removeCustomChargeFromLead = (index) => {
    setCustomCharges(customCharges.filter((_, i) => i !== index));
  };

  // Calculate totals for dialog
  const productsTotal = leadProducts.reduce((sum, p) => sum + (p.total || 0), 0);
  const customChargesTotal = customCharges.reduce((sum, c) => sum + (c.amount || 0), 0);
  const subtotal = productsTotal + (parseFloat(shippingCharge) || 0) + customChargesTotal;
  let discountAmount = 0;
  if (discountType === 'percentage' && discountValue > 0) {
    discountAmount = (subtotal * discountValue) / 100;
  } else if (discountType === 'fixed' && discountValue > 0) {
    discountAmount = parseFloat(discountValue) || 0;
  }
  const grandTotal = subtotal - discountAmount;

  const saveProducts = async () => {
    setSavingProducts(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/leads/${id}`, { 
        products: leadProducts,
        shipping_charge: parseFloat(shippingCharge) || 0,
        discount_type: discountType || null,
        discount_value: parseFloat(discountValue) || 0,
        custom_charges: customCharges,
        estimated_value: grandTotal || lead?.estimated_value,
        shipping_address: leadShippingAddress,
        billing_address: sameAsShipping ? leadShippingAddress : leadBillingAddress,
        billing_same_as_shipping: sameAsShipping
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Products and delivery details updated');
      setProductsDialogOpen(false);
      loadLead();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingProducts(false);
    }
  };

  // Server-side product search with debouncing
  useEffect(() => {
    const searchProducts = async () => {
      if (!searchProduct || searchProduct.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearchingProducts(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/inventory`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: searchProduct, limit: 20 }
        });
        setSearchResults(response.data || []);
      } catch (error) {
        console.error('Failed to search products:', error);
        setSearchResults([]);
      } finally {
        setSearchingProducts(false);
      }
    };
    
    // Debounce the search
    const timeoutId = setTimeout(searchProducts, 300);
    return () => clearTimeout(timeoutId);
  }, [searchProduct]);

  const handleUpdateLead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/leads/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lead updated');
      setEditDialogOpen(false);
      loadLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/leads/${id}`, { stage: newStage }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Stage updated');
      loadLead();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  // Open Create Order dialog with pre-filled data from lead
  const openCreateOrderDialog = () => {
    const products = lead?.products || [];
    if (products.length === 0) {
      toast.error('Please add products to the lead first');
      return;
    }
    
    const nameParts = (lead?.customer_name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Reset payment method states
    setPaymentMethod('cod');
    setPaymentNotes('');
    setMamoPayLink(null);
    setMamoPayLinkPaid(false);
    
    // Pre-fill shipping address from lead if available
    const shippingAddr = lead?.shipping_address || {};
    
    setOrderForm({
      customer_name: lead?.customer_name || '',
      customer_email: lead?.email || '',
      customer_phone: lead?.phone || '',
      shipping_address: {
        first_name: firstName,
        last_name: lastName,
        address1: shippingAddr.address1 || '',
        address2: shippingAddr.address2 || '',
        city: shippingAddr.city || '',
        province: shippingAddr.province || '',
        country: shippingAddr.country || 'United Arab Emirates',
        zip: shippingAddr.zip || ''
      },
      sync_to_shopify: true,
      payment_status: 'pending',
      notes: `Created from lead: ${lead?.customer_name}`
    });
    setCreateOrderDialogOpen(true);
  };

  // Calculate order total for MamoPay
  const calculateOrderTotal = () => {
    const productsTotal = (lead?.products || []).reduce((sum, p) => sum + (p.total || 0), 0);
    const shippingCost = lead?.shipping_charge || 0;
    const customChargesTotal = (lead?.custom_charges || []).reduce((sum, c) => sum + (c.amount || 0), 0);
    const subtotal = productsTotal + shippingCost + customChargesTotal;
    
    let discountAmount = 0;
    if (lead?.discount_type === 'percentage' && lead?.discount_value) {
      discountAmount = subtotal * lead.discount_value / 100;
    } else if (lead?.discount_type === 'fixed' && lead?.discount_value) {
      discountAmount = lead.discount_value;
    }
    
    return subtotal - discountAmount;
  };

  // Create MamoPay payment link
  const handleCreateMamoPayLink = async () => {
    if (!orderForm.customer_name) {
      toast.error('Customer name is required');
      return;
    }
    
    const total = calculateOrderTotal();
    if (total <= 0) {
      toast.error('Order total must be greater than 0');
      return;
    }
    
    setMamoPayLinkCreating(true);
    try {
      const token = localStorage.getItem('token');
      const nameParts = orderForm.customer_name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const response = await axios.post(
        `${API_BASE_URL}/mamopay/create-link`,
        {
          amount: total,
          title: `Payment for ${lead?.customer_name}`,
          first_name: firstName,
          last_name: lastName,
          email: orderForm.customer_email || '',
          phone: orderForm.customer_phone || '',
          reference_type: 'lead',
          reference_id: id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMamoPayLink(response.data);
      toast.success('Payment link created! Share it with the customer.');
      
      // Start polling for payment status
      startMamoPayPolling(response.data.mamopay_link_id);
    } catch (error) {
      console.error('MamoPay link creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setMamoPayLinkCreating(false);
    }
  };

  // Poll for MamoPay payment status
  const mamoPayPollingRef = React.useRef(null);
  
  const startMamoPayPolling = (linkId) => {
    if (mamoPayPollingRef.current) {
      clearInterval(mamoPayPollingRef.current);
    }
    
    let pollCount = 0;
    const maxPolls = 120; // 10 minutes at 5 second intervals
    
    mamoPayPollingRef.current = setInterval(async () => {
      pollCount++;
      if (pollCount > maxPolls) {
        clearInterval(mamoPayPollingRef.current);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/mamopay/link/${linkId}/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.is_paid) {
          setMamoPayLinkPaid(true);
          clearInterval(mamoPayPollingRef.current);
          toast.success('Payment received! You can now create the order.', { duration: 8000 });
        }
      } catch (error) {
        console.error('MamoPay status check error:', error);
      }
    }, 5000);
  };

  // Cleanup polling on unmount or dialog close
  React.useEffect(() => {
    return () => {
      if (mamoPayPollingRef.current) {
        clearInterval(mamoPayPollingRef.current);
      }
    };
  }, []);

  // Copy payment link to clipboard
  const copyPaymentLink = () => {
    if (mamoPayLink?.payment_url) {
      navigator.clipboard.writeText(mamoPayLink.payment_url);
      toast.success('Payment link copied to clipboard!');
    }
  };

  // Create order from lead
  const handleCreateOrder = async () => {
    // Validate shipping address
    if (!orderForm.shipping_address.address1) {
      toast.error('Please enter shipping address');
      return;
    }
    if (!orderForm.customer_email && orderForm.sync_to_shopify) {
      toast.error('Email is required for Shopify sync');
      return;
    }
    
    // Validate payment method specific requirements
    if (paymentMethod === 'request_payment' && !mamoPayLinkPaid) {
      toast.error('Please wait for customer to complete the payment');
      return;
    }
    
    setCreatingOrder(true);
    try {
      const token = localStorage.getItem('token');
      
      // Determine payment status and tags based on payment method
      let payment_status = 'pending';
      let tags = ['from-lead'];
      let orderNotes = orderForm.notes;
      
      if (paymentMethod === 'cod') {
        payment_status = 'pending';
        tags.push('COD');
      } else if (paymentMethod === 'request_payment') {
        payment_status = 'paid';
        tags.push('MamoPay');
        if (mamoPayLink?.mamopay_link_id) {
          orderNotes = `${orderForm.notes}\n\nMamoPay Link ID: ${mamoPayLink.mamopay_link_id}`;
        }
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/leads/${id}/create-order`,
        {
          ...orderForm,
          notes: orderNotes,
          payment_status,
          tags,
          products: lead?.products,
          shipping_cost: lead?.shipping_charge || 0,
          discount_type: lead?.discount_type,
          discount_value: lead?.discount_value,
          custom_charges: lead?.custom_charges,
          mamopay_link_id: paymentMethod === 'request_payment' ? mamoPayLink?.mamopay_link_id : null,
          mamopay_paid: paymentMethod === 'request_payment'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      // Stop polling
      if (mamoPayPollingRef.current) {
        clearInterval(mamoPayPollingRef.current);
      }
      
      if (result.shopify_synced) {
        toast.success(`Order ${result.order?.order_number} created and synced to Shopify!`);
      } else if (result.shopify_error) {
        toast.warning(`Order created but Shopify sync failed: ${result.shopify_error}`);
      } else {
        toast.success(`Order ${result.order?.order_number} created successfully!`);
      }
      
      setCreateOrderDialogOpen(false);
      loadLead();
      
      // Navigate to order details
      if (result.order?.id) {
        navigate(`/orders?search=${result.order.order_number}`);
      }
    } catch (error) {
      console.error('Create order error:', error);
      const errorDetail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) or string errors
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
      } else if (typeof errorDetail === 'object') {
        toast.error(JSON.stringify(errorDetail));
      } else {
        toast.error(errorDetail || 'Failed to create order');
      }
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/leads/${id}/comments`, 
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comment added');
      setNewComment('');
      setCommentDialogOpen(false);
      loadLead();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/leads/${id}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Comment deleted');
      loadLead();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleAddReminder = async () => {
    if (!newReminder.datetime || !newReminder.message) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/leads/${id}/reminders`, newReminder, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Reminder set');
      setNewReminder({ datetime: '', message: '' });
      setReminderDialogOpen(false);
      loadLead();
    } catch (error) {
      toast.error('Failed to set reminder');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/leads/${id}/reminders/${reminderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Reminder deleted');
      loadLead();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleAssignLead = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/leads/${id}/assign`, 
        { assigned_to: selectedUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead assigned');
      setAssignDialogOpen(false);
      setSelectedUserId('');
      loadLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign lead');
    }
  };

  const handleUploadFile = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      await axios.post(`${API_BASE_URL}/leads/${id}/attachments/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      toast.success('File uploaded');
      setUploadFile(null);
      setUploadDialogOpen(false);
      loadLead();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/leads/${id}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Attachment deleted');
      loadLead();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDeleteLead = async () => {
    if (!window.confirm(`Are you sure you want to delete this lead "${lead.customer_name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lead deleted successfully');
      navigate('/leads');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const openEditDialog = () => {
    setEditForm({
      customer_name: lead.customer_name,
      email: lead.email,
      phone: lead.phone,
      source: lead.source,
      estimated_value: lead.estimated_value,
      notes: lead.notes
    });
    setEditDialogOpen(true);
  };

  // Check MamoPay payment link status
  const handleCheckPaymentStatus = async (mamoPayLinkId) => {
    if (!mamoPayLinkId) return;
    
    setCheckingPaymentStatus(prev => ({ ...prev, [mamoPayLinkId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/mamopay/link/${mamoPayLinkId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.is_paid) {
        toast.success('Payment received! Lead updated.');
        loadLead(); // Reload lead to get updated data
      } else {
        toast.info('Payment not yet received');
      }
    } catch (error) {
      console.error('Status check failed:', error);
      toast.error('Failed to check payment status');
    } finally {
      setCheckingPaymentStatus(prev => ({ ...prev, [mamoPayLinkId]: false }));
    }
  };

  // Copy payment link to clipboard
  const handleCopyPaymentUrl = (url) => {
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success('Payment link copied!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wt-gold" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 mx-auto text-wt-text-muted mb-4" />
        <h2 className="text-xl text-wt-text-primary">Lead not found</h2>
      </div>
    );
  }

  const stageConfig = LEAD_STAGES.find(s => s.id === lead.stage) || LEAD_STAGES[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/leads')}
            className="text-wt-text-secondary hover:text-wt-text-primary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-wt-text-primary">
              {lead.customer_name}
            </h1>
            <p className="text-wt-text-secondary text-sm">Lead ID: {lead.id.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {isAdmin && (
            <Button
              onClick={() => setAssignDialogOpen(true)}
              variant="outline"
              size="sm"
              className="border-wt-border text-wt-text-primary hover:bg-wt-subtle"
            >
              <UserPlus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Assign</span>
            </Button>
          )}
          <Button
            onClick={openEditDialog}
            size="sm"
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          >
            <Edit2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          {canCreatePaymentLink && (
            <PaymentLinkButton
              referenceType="lead"
              referenceId={lead.id}
              amount={lead.estimated_value}
              customerName={lead.customer_name}
              customerEmail={lead.email}
              customerPhone={lead.phone}
              title={`Quote for ${lead.customer_name}`}
              existingLinks={lead.payment_links || []}
              onLinkCreated={() => loadLead()}
              onPaymentConfirmed={() => loadLead()}
            />
          )}
          {/* Create Order Button */}
          <Button
            onClick={() => window.open(`/product-availability?search=${encodeURIComponent((lead?.products?.[0]?.sku) || '')}`, '_blank')}
            size="sm"
            variant="outline"
            className="border-wt-gold/40 text-wt-gold"
            data-testid="lead-check-availability-btn"
          >
            <Package className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Availability</span>
          </Button>
          <Button
            onClick={openCreateOrderDialog}
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="create-order-btn"
          >
            <ShoppingCart className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Create Order</span>
          </Button>
          {isAdmin && (
            <Button
              onClick={handleDeleteLead}
              variant="outline"
              size="sm"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500"
              data-testid="delete-lead-button"
            >
              <Trash2 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lead Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Lead Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-wt-gold" />
                <div>
                  <p className="text-xs text-wt-text-muted">Email</p>
                  <p className="text-wt-text-primary">{lead.email || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-wt-gold" />
                <div>
                  <p className="text-xs text-wt-text-muted">Phone</p>
                  <p className="text-wt-text-primary">{lead.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-wt-gold" />
                <div>
                  <p className="text-xs text-wt-text-muted">Estimated Value</p>
                  <p className="text-wt-text-primary font-semibold">
                    {lead.estimated_value ? formatCurrency(lead.estimated_value) : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-wt-gold" />
                <div>
                  <p className="text-xs text-wt-text-muted">Source</p>
                  <p className="text-wt-text-primary">{lead.source || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-wt-gold" />
                <div>
                  <p className="text-xs text-wt-text-muted">Assigned To</p>
                  <p className="text-wt-text-primary">{lead.assigned_to_name || 'Unassigned'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-wt-gold" />
                <div>
                  <p className="text-xs text-wt-text-muted">Created</p>
                  <p className="text-wt-text-primary">{formatDate(lead.created_at)}</p>
                </div>
              </div>
            </div>
            
            {lead.notes && (
              <div className="mt-4 pt-4 border-t border-wt-border">
                <p className="text-xs text-wt-text-muted mb-1">Notes</p>
                <p className="text-wt-text-secondary">{lead.notes}</p>
              </div>
            )}
          </motion.div>

          {/* Products of Interest */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-purple-400" />
                Products of Interest
              </h2>
              <Button
                size="sm"
                onClick={() => setProductsDialogOpen(true)}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Manage Products
              </Button>
            </div>

            {(!lead.products || lead.products.length === 0) && !lead.shipping_charge && (!lead.custom_charges || lead.custom_charges.length === 0) ? (
              <p className="text-wt-text-muted text-center py-6">No products or charges added yet</p>
            ) : (
              <div className="space-y-3">
                {/* Products */}
                {lead.products && lead.products.length > 0 && lead.products.map((product, index) => (
                  <div key={index} className="bg-wt-subtle rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-wt-text-primary font-medium">{product.name}</span>
                        <span className="text-wt-text-muted text-xs ml-2">({product.sku})</span>
                      </div>
                      <span className="text-wt-gold font-semibold">{formatCurrency(product.total)}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-wt-text-muted">Qty:</span>
                        <span className="text-wt-text-primary ml-1">{product.quantity}</span>
                      </div>
                      <div>
                        <span className="text-wt-text-muted">Unit Price:</span>
                        <span className="text-wt-text-primary ml-1">{formatCurrency(product.unit_price)}</span>
                      </div>
                      <div>
                        <span className="text-wt-text-muted">Customization:</span>
                        <span className="text-wt-text-primary ml-1">{formatCurrency(product.customization_charge || 0)}</span>
                      </div>
                      <div>
                        <span className="text-wt-text-muted">Total:</span>
                        <span className="text-wt-gold ml-1 font-medium">{formatCurrency(product.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Summary */}
                <div className="border-t border-wt-border pt-4 mt-4 space-y-2">
                  {lead.products && lead.products.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-wt-text-muted">Products Total:</span>
                      <span className="text-wt-text-primary">{formatCurrency(lead.products.reduce((sum, p) => sum + (p.total || 0), 0))}</span>
                    </div>
                  )}
                  {lead.shipping_charge > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-wt-text-muted">Shipping:</span>
                      <span className="text-wt-text-primary">{formatCurrency(lead.shipping_charge)}</span>
                    </div>
                  )}
                  {lead.custom_charges && lead.custom_charges.length > 0 && lead.custom_charges.map((charge, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-wt-text-muted">{charge.name}:</span>
                      <span className="text-wt-text-primary">{formatCurrency(charge.amount)}</span>
                    </div>
                  ))}
                  {lead.discount_value > 0 && (
                    <div className="flex justify-between text-sm text-green-400">
                      <span>Discount ({lead.discount_type === 'percentage' ? `${lead.discount_value}%` : 'Fixed'}):</span>
                      <span>-{formatCurrency(
                        lead.discount_type === 'percentage' 
                          ? (((lead.products?.reduce((sum, p) => sum + (p.total || 0), 0) || 0) + (lead.shipping_charge || 0) + (lead.custom_charges?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0)) * lead.discount_value / 100)
                          : lead.discount_value
                      )}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t border-wt-border">
                    <span className="text-wt-text-primary">Grand Total:</span>
                    <span className="text-wt-gold">
                      {formatCurrency((() => {
                        const productsTotal = lead.products?.reduce((sum, p) => sum + (p.total || 0), 0) || 0;
                        const shippingCharge = lead.shipping_charge || 0;
                        const customChargesTotal = lead.custom_charges?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0;
                        const subtotal = productsTotal + shippingCharge + customChargesTotal;
                        let discountAmount = 0;
                        if (lead.discount_type === 'percentage' && lead.discount_value > 0) {
                          discountAmount = (subtotal * lead.discount_value) / 100;
                        } else if (lead.discount_type === 'fixed' && lead.discount_value > 0) {
                          discountAmount = lead.discount_value;
                        }
                        return subtotal - discountAmount;
                      })())}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Stage Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Pipeline Stage</h2>
            <div className="flex flex-wrap gap-2">
              {LEAD_STAGES.map((stage) => (
                <button
                  key={stage.id}
                  onClick={() => handleStageChange(stage.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    lead.stage === stage.id
                      ? `${stage.color} text-white`
                      : 'bg-wt-subtle text-wt-text-secondary hover:bg-wt-border'
                  }`}
                >
                  {stage.label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Activity Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-wt-text-primary">
                Activity Timeline
              </h2>
              <Button
                size="sm"
                onClick={() => setCommentDialogOpen(true)}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </div>
            
            {(!lead.comments || lead.comments.length === 0) ? (
              <p className="text-wt-text-muted text-center py-8">No activity yet</p>
            ) : (
              <div className="space-y-4">
                {lead.comments.slice().reverse().map((comment) => {
                  // Check if this is a MamoPay payment link activity
                  const isMamoPayLink = comment.type === 'mamopay_link_created';
                  const isPaymentReceived = comment.type === 'payment_received';
                  const isPaid = comment.is_paid;
                  
                  return (
                    <div 
                      key={comment.id} 
                      className={`flex gap-3 border-l-2 pl-4 ${
                        isPaymentReceived ? 'border-green-500/50' :
                        isMamoPayLink ? (isPaid ? 'border-green-500/50' : 'border-blue-500/50') :
                        'border-wt-gold/30'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {isMamoPayLink && (
                              <CreditCard className={`h-4 w-4 ${isPaid ? 'text-green-400' : 'text-blue-400'}`} />
                            )}
                            {isPaymentReceived && (
                              <CheckCircle className="h-4 w-4 text-green-400" />
                            )}
                            <p className="text-sm font-medium text-wt-text-primary">
                              {comment.author}
                            </p>
                          </div>
                          {isAdmin && !isMamoPayLink && !isPaymentReceived && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-wt-text-muted hover:text-red-400 h-6 w-6 p-0"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        
                        <p className="text-wt-text-secondary text-sm mt-1">{comment.content}</p>
                        
                        {/* MamoPay payment link actions */}
                        {isMamoPayLink && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {isPaid ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                                <CheckCircle className="h-3 w-3" />
                                Paid
                              </span>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCheckPaymentStatus(comment.mamopay_link_id)}
                                  disabled={checkingPaymentStatus[comment.mamopay_link_id]}
                                  className="h-7 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                  data-testid={`check-status-${comment.mamopay_link_id}`}
                                >
                                  <RefreshCw className={`h-3 w-3 mr-1 ${checkingPaymentStatus[comment.mamopay_link_id] ? 'animate-spin' : ''}`} />
                                  {checkingPaymentStatus[comment.mamopay_link_id] ? 'Checking...' : 'Check Status'}
                                </Button>
                                {comment.payment_url && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleCopyPaymentUrl(comment.payment_url)}
                                    className="h-7 text-xs text-wt-text-muted hover:text-wt-text-primary"
                                    data-testid={`copy-link-${comment.mamopay_link_id}`}
                                  >
                                    <Copy className="h-3 w-3 mr-1" />
                                    Copy Link
                                  </Button>
                                )}
                                <span className="text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full">
                                  Pending
                                </span>
                              </>
                            )}
                            {comment.amount && (
                              <span className="text-xs text-wt-gold font-medium">
                                {formatCurrency(comment.amount)}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Payment received badge */}
                        {isPaymentReceived && comment.amount && (
                          <div className="mt-2">
                            <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                              <CheckCircle className="h-3 w-3" />
                              Paid - {formatCurrency(comment.amount)}
                            </span>
                          </div>
                        )}
                        
                        <p className="text-xs text-wt-text-muted mt-1">
                          {formatDateTime(comment.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`${stageConfig.color} rounded-xl p-6 text-white`}
          >
            <p className="text-sm opacity-80">Current Stage</p>
            <p className="text-2xl font-bold">{stageConfig.label}</p>
          </motion.div>

          {/* Reminders */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-wt-text-primary flex items-center gap-2">
                <Bell className="h-4 w-4 text-wt-gold" />
                Reminders
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setReminderDialogOpen(true)}
                className="text-wt-gold hover:text-wt-gold-hover"
              >
                <Clock className="h-4 w-4" />
              </Button>
            </div>
            
            {(!lead.reminders || lead.reminders.length === 0) ? (
              <p className="text-wt-text-muted text-sm">No reminders set</p>
            ) : (
              <div className="space-y-3">
                {lead.reminders.map((reminder) => {
                  const isPast = new Date(reminder.datetime) < new Date();
                  return (
                    <div key={reminder.id} className={`rounded-lg p-3 ${reminder.is_sent ? 'bg-green-500/10 border border-green-500/20' : isPast ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-wt-subtle'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-wt-text-primary">{reminder.message}</p>
                          <p className="text-xs text-wt-text-muted mt-1">
                            {formatDateTime(reminder.datetime)}
                          </p>
                          {reminder.is_sent && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-400 mt-1">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
                              Notification sent
                            </span>
                          )}
                          {!reminder.is_sent && isPast && (
                            <span className="inline-flex items-center gap-1 text-xs text-orange-400 mt-1">
                              <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
                              Overdue
                            </span>
                          )}
                        </div>
                        {!reminder.is_sent && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteReminder(reminder.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                            data-testid={`delete-reminder-${reminder.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Attachments */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-wt-text-primary flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-wt-gold" />
                Attachments
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setUploadDialogOpen(true)}
                className="text-wt-gold hover:text-wt-gold-hover"
              >
                <Upload className="h-4 w-4" />
              </Button>
            </div>
            
            {(!lead.attachments || lead.attachments.length === 0) ? (
              <p className="text-wt-text-muted text-sm">No attachments</p>
            ) : (
              <div className="space-y-2">
                {lead.attachments.map((file) => (
                  <div key={file.id} className="flex items-center justify-between bg-wt-subtle rounded-lg p-3">
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-wt-text-primary hover:text-wt-gold truncate flex-1"
                    >
                      {file.name}
                    </a>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteAttachment(file.id)}
                      className="text-wt-text-muted hover:text-red-400 h-6 w-6 p-0 ml-2"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Edit Lead
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">Customer Name</Label>
              <Input
                value={editForm.customer_name || ''}
                onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Email</Label>
                <Input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Phone</Label>
                <Input
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Source</Label>
                <Input
                  value={editForm.source || ''}
                  onChange={(e) => setEditForm({...editForm, source: e.target.value})}
                  className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Estimated Value</Label>
                <Input
                  type="number"
                  value={editForm.estimated_value || ''}
                  onChange={(e) => setEditForm({...editForm, estimated_value: parseFloat(e.target.value)})}
                  className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            <div>
              <Label className="text-wt-text-primary">Notes</Label>
              <Textarea
                value={editForm.notes || ''}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleUpdateLead} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Add Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write your note..."
              className="bg-wt-bg border-wt-border text-wt-text-primary"
              rows={4}
            />
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setCommentDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleAddComment} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                <Send className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Set Reminder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">Date & Time</Label>
              <DateTimeInput
                value={newReminder.datetime}
                onChange={(e) => setNewReminder({...newReminder, datetime: e.target.value})}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div>
              <Label className="text-wt-text-primary">Message</Label>
              <Input
                value={newReminder.message}
                onChange={(e) => setNewReminder({...newReminder, message: e.target.value})}
                placeholder="Reminder message..."
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setReminderDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleAddReminder} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                <Bell className="h-4 w-4 mr-2" />
                Set Reminder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Lead Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Assign Lead
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Choose a user" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {salesUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setAssignDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleAssignLead} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                <UserPlus className="h-4 w-4 mr-2" />
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Attachment Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Upload Attachment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">File</Label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setUploadDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleUploadFile} disabled={uploading} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Products Management Dialog */}
      <Dialog open={productsDialogOpen} onOpenChange={setProductsDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-xl flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-400" />
              Manage Products of Interest
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Product Search */}
            <div className="relative">
              <Label className="text-wt-text-primary mb-2 block">Search & Add Products</Label>
              <div className="relative">
                <Input
                  placeholder="Search products by name or SKU (min 2 characters)..."
                  value={searchProduct}
                  onChange={(e) => setSearchProduct(e.target.value)}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
                {searchingProducts && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-wt-gold border-t-transparent"></div>
                  </div>
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        addProductToLead(item);
                        setSearchProduct('');
                        setSearchResults([]);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-wt-subtle flex items-center justify-between"
                    >
                      <div>
                        <span className="text-wt-text-primary">{item.name}</span>
                        <span className="text-wt-text-muted text-xs ml-2">({item.sku})</span>
                      </div>
                      <span className="text-wt-gold text-sm">{formatCurrency(item.selling_price)}</span>
                    </button>
                  ))}
                </div>
              )}
              {searchProduct.length >= 2 && !searchingProducts && searchResults.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg shadow-lg p-3 text-center text-wt-text-muted text-sm">
                  No products found for "{searchProduct}"
                </div>
              )}
            </div>

            {/* Selected Products */}
            {leadProducts.length > 0 ? (
              <div className="space-y-3">
                <Label className="text-wt-text-primary">Selected Products</Label>
                {leadProducts.map((product, index) => (
                  <div key={index} className="bg-wt-subtle rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-wt-text-primary font-medium">{product.name}</span>
                        <span className="text-wt-text-muted text-xs ml-2">({product.sku})</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLeadProduct(index)}
                        className="text-red-400 hover:text-red-500 h-7 w-7 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-wt-text-muted">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={product.quantity}
                          onChange={(e) => updateLeadProduct(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="bg-wt-bg border-wt-border text-wt-text-primary h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">Unit Price</Label>
                        <Input
                          type="number"
                          min="0"
                          value={product.unit_price}
                          onChange={(e) => updateLeadProduct(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="bg-wt-bg border-wt-border text-wt-text-primary h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">Customization</Label>
                        <Input
                          type="number"
                          min="0"
                          value={product.customization_charge || 0}
                          onChange={(e) => updateLeadProduct(index, 'customization_charge', parseFloat(e.target.value) || 0)}
                          className="bg-wt-bg border-wt-border text-wt-text-primary h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">Total</Label>
                        <div className="h-9 flex items-center text-wt-gold font-semibold">
                          {formatCurrency(product.total)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Grand Total */}
                <div className="flex justify-end pt-3 border-t border-wt-border">
                  <div>
                    <span className="text-wt-text-muted">Products Total:</span>
                    <span className="text-wt-gold font-bold text-xl ml-2">
                      {formatCurrency(productsTotal)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-wt-text-muted text-center py-4">No products selected. Search and add products above.</p>
            )}

            {/* Additional Charges Section */}
            <div className="border-t border-wt-border pt-4 space-y-4">
              <Label className="text-wt-text-primary flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Additional Charges & Discounts
              </Label>

              <div className="grid grid-cols-2 gap-4">
                {/* Shipping Charge */}
                <div>
                  <Label className="text-xs text-wt-text-muted flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    Shipping Charge
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={shippingCharge}
                    onChange={(e) => setShippingCharge(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>

                {/* Discount */}
                <div>
                  <Label className="text-xs text-wt-text-muted flex items-center gap-1">
                    <Percent className="h-3 w-3" />
                    Discount
                  </Label>
                  <div className="flex gap-2">
                    <Select value={discountType} onValueChange={setDiscountType}>
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary w-28">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        <SelectItem value="percentage" className="text-wt-text-primary">%</SelectItem>
                        <SelectItem value="fixed" className="text-wt-text-primary">Fixed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                      disabled={!discountType}
                    />
                  </div>
                </div>
              </div>

              {/* Custom Charges */}
              <div>
                <Label className="text-xs text-wt-text-muted mb-2 block">Custom Charges (e.g., Installation, Assembly)</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder="Charge name"
                    value={newCustomCharge.name}
                    onChange={(e) => setNewCustomCharge({ ...newCustomCharge, name: e.target.value })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                  />
                  <Input
                    type="number"
                    min="0"
                    placeholder="Amount"
                    value={newCustomCharge.amount || ''}
                    onChange={(e) => setNewCustomCharge({ ...newCustomCharge, amount: parseFloat(e.target.value) || 0 })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary w-28"
                  />
                  <Button
                    type="button"
                    onClick={addCustomChargeToLead}
                    size="sm"
                    className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {customCharges.length > 0 && (
                  <div className="space-y-1">
                    {customCharges.map((charge, index) => (
                      <div key={index} className="flex items-center justify-between bg-wt-subtle rounded px-3 py-2">
                        <span className="text-wt-text-primary text-sm">{charge.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-wt-gold text-sm">{formatCurrency(charge.amount)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCustomChargeFromLead(index)}
                            className="text-red-400 hover:text-red-500 h-5 w-5 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Summary */}
              {(productsTotal > 0 || shippingCharge > 0 || customChargesTotal > 0 || discountAmount > 0) && (
                <div className="border-t border-wt-border pt-3 space-y-1">
                  {productsTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-wt-text-muted">Products Total:</span>
                      <span className="text-wt-text-primary">{formatCurrency(productsTotal)}</span>
                    </div>
                  )}
                  {shippingCharge > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-wt-text-muted">Shipping:</span>
                      <span className="text-wt-text-primary">{formatCurrency(shippingCharge)}</span>
                    </div>
                  )}
                  {customChargesTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-wt-text-muted">Custom Charges:</span>
                      <span className="text-wt-text-primary">{formatCurrency(customChargesTotal)}</span>
                    </div>
                  )}
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-400">
                      <span>Discount ({discountType === 'percentage' ? `${discountValue}%` : 'Fixed'}):</span>
                      <span>-{formatCurrency(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-semibold pt-2 border-t border-wt-border">
                    <span className="text-wt-text-primary">Grand Total:</span>
                    <span className="text-wt-gold">{formatCurrency(grandTotal)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setProductsDialogOpen(false)}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={saveProducts}
                disabled={savingProducts}
                className="bg-wt-gold text-black hover:bg-wt-gold-hover"
              >
                {savingProducts ? 'Saving...' : 'Save Products'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog open={createOrderDialogOpen} onOpenChange={setCreateOrderDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-green-500" />
              Create Order from Lead
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Customer Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-wt-text-primary">Customer Information</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-wt-text-muted text-xs">Name</Label>
                  <Input
                    value={orderForm.customer_name}
                    onChange={(e) => setOrderForm({...orderForm, customer_name: e.target.value})}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">Email *</Label>
                  <Input
                    type="email"
                    value={orderForm.customer_email}
                    onChange={(e) => setOrderForm({...orderForm, customer_email: e.target.value})}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Email (required for Shopify)"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">Phone</Label>
                  <Input
                    value={orderForm.customer_phone}
                    onChange={(e) => setOrderForm({...orderForm, customer_phone: e.target.value})}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Phone"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Shipping Address
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-wt-text-muted text-xs">First Name</Label>
                  <Input
                    value={orderForm.shipping_address.first_name}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, first_name: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">Last Name</Label>
                  <Input
                    value={orderForm.shipping_address.last_name}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, last_name: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-wt-text-muted text-xs">Address Line 1 *</Label>
                  <Input
                    value={orderForm.shipping_address.address1}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, address1: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Street address"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-wt-text-muted text-xs">Address Line 2</Label>
                  <Input
                    value={orderForm.shipping_address.address2}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, address2: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Apartment, suite, unit (optional)"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">City</Label>
                  <Input
                    value={orderForm.shipping_address.city}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, city: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">Province/State</Label>
                  <Input
                    value={orderForm.shipping_address.province}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, province: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Province"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">Country</Label>
                  <Input
                    value={orderForm.shipping_address.country}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, country: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted text-xs">ZIP/Postal Code</Label>
                  <Input
                    value={orderForm.shipping_address.zip}
                    onChange={(e) => setOrderForm({
                      ...orderForm, 
                      shipping_address: {...orderForm.shipping_address, zip: e.target.value}
                    })}
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Postal code"
                  />
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="space-y-2 p-3 bg-wt-subtle rounded-lg">
              <h3 className="text-sm font-medium text-wt-text-primary">Order Summary</h3>
              <div className="space-y-1 text-sm">
                {lead?.products?.map((p, i) => (
                  <div key={i} className="flex justify-between text-wt-text-muted">
                    <span>{p.product_title || p.name} x{p.quantity || 1}</span>
                    <span>{formatCurrency((p.unit_price || p.price || 0) * (p.quantity || 1))}</span>
                  </div>
                ))}
                {lead?.shipping_charge > 0 && (
                  <div className="flex justify-between text-wt-text-muted">
                    <span>Shipping</span>
                    <span>{formatCurrency(lead.shipping_charge)}</span>
                  </div>
                )}
                {lead?.custom_charges?.map((c, i) => (
                  <div key={i} className="flex justify-between text-wt-text-muted">
                    <span>{c.name}</span>
                    <span>{formatCurrency(c.amount)}</span>
                  </div>
                ))}
                {lead?.discount_value > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Discount ({lead.discount_type === 'percentage' ? `${lead.discount_value}%` : 'Fixed'})</span>
                    <span>-{formatCurrency(
                      lead.discount_type === 'percentage' 
                        ? (calculateOrderTotal() + (lead.discount_type === 'percentage' ? calculateOrderTotal() * lead.discount_value / (100 - lead.discount_value) : lead.discount_value)) * lead.discount_value / 100
                        : lead.discount_value
                    )}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium text-wt-text-primary pt-2 border-t border-wt-border">
                  <span>Total</span>
                  <span className="text-wt-gold">{formatCurrency(calculateOrderTotal())}</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment Method
              </h3>
              
              <div className="space-y-2">
                {/* COD Option */}
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  paymentMethod === 'cod' 
                    ? 'border-yellow-500 bg-yellow-500/10' 
                    : 'border-wt-border hover:border-wt-gold/50'
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cod"
                    checked={paymentMethod === 'cod'}
                    onChange={() => setPaymentMethod('cod')}
                    className="mt-1 accent-yellow-500"
                  />
                  <div className="flex-1">
                    <span className="text-wt-text-primary font-medium">Cash on Delivery (COD)</span>
                    <p className="text-xs text-wt-text-muted">Customer will pay when order is delivered</p>
                  </div>
                </label>

                {/* Request Payment Option */}
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  paymentMethod === 'request_payment' 
                    ? 'border-blue-500 bg-blue-500/10' 
                    : 'border-wt-border hover:border-wt-gold/50'
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="request_payment"
                    checked={paymentMethod === 'request_payment'}
                    onChange={() => setPaymentMethod('request_payment')}
                    className="mt-1 accent-blue-500"
                  />
                  <div className="flex-1">
                    <span className="text-wt-text-primary font-medium">Request Payment (MamoPay)</span>
                    <p className="text-xs text-wt-text-muted">Create a payment link to share with customer</p>
                  </div>
                </label>

                {/* MamoPay Section - show only when Request Payment is selected */}
                {paymentMethod === 'request_payment' && (
                  <div className="ml-6 p-3 bg-wt-subtle rounded-lg space-y-3">
                    {!mamoPayLink ? (
                      <Button
                        onClick={handleCreateMamoPayLink}
                        disabled={mamoPayLinkCreating}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {mamoPayLinkCreating ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Creating Link...
                          </>
                        ) : (
                          <>
                            <Link2 className="h-4 w-4 mr-2" />
                            Create Payment Link
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 bg-wt-bg rounded border border-wt-border">
                          <span className="text-xs text-wt-text-muted truncate flex-1 mr-2">
                            {mamoPayLink.payment_url}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={copyPaymentLink}
                            className="text-wt-gold hover:text-wt-gold/80"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="text-center">
                          {mamoPayLinkPaid ? (
                            <div className="flex items-center justify-center gap-2 text-green-400">
                              <CheckCircle className="h-5 w-5" />
                              <span className="font-medium">Payment Received!</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 text-yellow-400">
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              <span className="text-sm">Waiting for payment...</span>
                            </div>
                          )}
                        </div>
                        
                        <p className="text-xs text-wt-text-muted text-center">
                          Share this link with customer. Order will be created once payment is confirmed.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">Sync to Shopify</Label>
                  <p className="text-xs text-wt-text-muted">Create order in Shopify as well</p>
                </div>
                <input
                  type="checkbox"
                  checked={orderForm.sync_to_shopify}
                  onChange={(e) => setOrderForm({...orderForm, sync_to_shopify: e.target.checked})}
                  className="w-5 h-5 accent-wt-gold"
                />
              </div>
              <div>
                <Label className="text-wt-text-muted text-xs">Notes</Label>
                <Textarea
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                  rows={2}
                  placeholder="Order notes"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
              <Button
                variant="ghost"
                onClick={() => {
                  if (mamoPayPollingRef.current) {
                    clearInterval(mamoPayPollingRef.current);
                  }
                  setCreateOrderDialogOpen(false);
                }}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateOrder}
                disabled={
                  creatingOrder || 
                  (paymentMethod === 'request_payment' && !mamoPayLinkPaid)
                }
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                data-testid="confirm-create-order"
              >
                {creatingOrder ? 'Creating...' : 'Create Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetailPage;
